import { normalize } from "normalizr";
import { SagaIterator } from "redux-saga";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { UsersCache } from "../../../EasyProjects/ActivityList/Cache/UsersCache";
import { User } from "../../../normalizr/entities";
import { availableUser, availableUsersSchema } from "../../../normalizr/usersSchema";
import { userInvited } from "../../../UserInvite/actions/userInvited";
import { doReconstructEntities, doUpdateEntities } from "../../Middleware/apiMiddleware";
import { handleApiError } from "../../Sagas/effects/handleApiError";
import { withCallback } from "../../Sagas/redux-saga-callback";
import { ActionsTypes } from "../actionTypes";
import { LoadAvailableUsersAction } from "./actions";
import { getAllUsers } from "./selectors";

export function* watchAvailableUsersRequest() {
    function* loadUsers(apiAction: F<PromiseLike<User[]>>, forceReload?: boolean): SagaIterator {
        const data: User[] = yield call(() => apiAction().then(x => x));
        const users = normalize(data, availableUsersSchema);

        forceReload
            ? yield put(doReconstructEntities(users.entities, availableUser.key))
            : yield put(doUpdateEntities(users.entities, availableUser.key));
    }

    function* loadAvailableUsers({
        requiredUserIds,
        missedUsersLoader,
        beforeRequestAction,
        afterRequestAction,
        forceReload,
    }: LoadAvailableUsersAction) {
        let existingUsers: ReturnType<typeof getAllUsers> = yield select(getAllUsers);

        try {
            if (beforeRequestAction) {
                yield put(beforeRequestAction());
            }

            if (!existingUsers || forceReload) {
                yield call(loadUsers, () => UsersCache.inst.getData(), forceReload);
            }

            if (requiredUserIds && requiredUserIds.length) {
                existingUsers = yield select(getAllUsers);
                const missedUsers = requiredUserIds.filter(id => !existingUsers[id]);
                if (missedUsers.length) {
                    const loader = missedUsersLoader || UsersCache.inst.loadSafely;
                    yield call(loadUsers, () => loader(missedUsers));
                }
            }

            if (afterRequestAction) {
                yield put(afterRequestAction());
            }
        } catch (e) {
            yield call(handleApiError, e);
        }
    }

    yield takeLatest(ActionsTypes.LOAD_USERS, withCallback(loadAvailableUsers));
}

export function* watchInvitedUsers() {
    function* addInvitedUser({ payload }: ReturnType<typeof userInvited>) {
        let existingUsers: ReturnType<typeof getAllUsers> = yield select(getAllUsers);
        let alreadyLoaded = !!existingUsers;

        // the module can only be loaded for teams, in whitch case we should not react on user's invitation,
        // otherwise the new user will be added into store, and loadAvailableUsers won't load users on demand.
        if (alreadyLoaded) {
            // for BoardTaskViewModel: after closing UsersSelect, the viewer uses UsersCache to render usernames.
            UsersCache.inst.add(payload);

            const users = normalize(payload, availableUser);
            yield put(doUpdateEntities(users.entities, availableUser.key));
        } else {
            // just in case, if the cache has been already filled somehow
            UsersCache.invalidate();
        }
    }

    yield takeEvery(userInvited, addInvitedUser);
}
